import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LockIcon, SendIcon, SettingsIcon } from 'lucide-react';
import { AnnaAvatar } from '../components/avatar/AnnaAvatar';
import { HouseModeChip } from '../components/ui/HouseModeChip';
import { NoodstopButton } from '../components/ui/NoodstopButton';
import { NoodstopSheet } from '../components/ui/NoodstopSheet';
import { AgentMark } from '../components/ui/AgentMark';
import { PhoneProposal } from '../components/phone/PhoneProposal';
import { MomentControl } from '../components/wall/MomentControl';
import { useDb } from '../hooks/useDb';
import { db } from '../utils/db';
import { activeTasks, avatarThreads, nextEvent, pendingProposals } from '../utils/dbSelectors';
import { getAgent } from '../utils/agents';
import { resolveAvatarState } from '../utils/houseRules';
import { Segmented } from '../components/ui/Segmented';
import { AnnaWordmark } from '../components/ui/AnnaWordmark';
import { interpretDesk } from '../utils/deskIntents';
import { interpret } from '../utils/intents';
import { applyStageEffect } from '../utils/stageEffects';
import { IDLE } from '../data/palette';
import type { HouseMode } from '../types/anna';
import type { Activity } from '../types/stage';
const EASE = [0.23, 1, 0.32, 1] as const;
const PHONE_MODES: HouseMode[] = ['Vrij', 'Focus', 'Afwezig', 'Slapen'];

/** Telefoon: Anna onderweg. Wat wacht op jou eerst, dan je huis en wie er werkt. */
export function Telefoon() {
  const meta = useDb(t => t.meta);
  const pending = useDb(pendingProposals);
  const tasks = useDb(activeTasks);
  const threads = useDb(avatarThreads);
  const next = useDb(nextEvent);
  const rooms = useDb(t => t.rooms);
  const locks = useDb(t => t.devices.filter(d => d.kind === 'slot' || d.kind === 'alarm'));
  const paused = meta.pausedAgents;
  const stopped = meta.stopped;
  const [question, setQuestion] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [activity, setActivity] = useState<Activity>('idle');
  const avatarState = resolveAvatarState({
    stopped,
    activity,
    transient: null,
    rest: meta.mode === 'Slapen' ? 'slaapt' : 'rust'
  });
  const home = rooms.filter(r => r.presence !== 'leeg');
  const ask = (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setQuestion('');
    setActivity('thinking');
    setReply(null);
    // Same brain as the wall and the desk: one interpretation, one set of house effects.
    const state = db.getState();
    const reply = interpretDesk(q, state);
    const home = interpret(q, {
      mode: state.meta.mode,
      waiting: pending.length,
      roomIds: rooms.map(r => r.id)
    });
    window.setTimeout(() => {
      setActivity('speaking');
      applyStageEffect(reply.effect);
      if (home.action === 'focus') db.setMode('Focus', 'telefoon');
      if (home.action === 'slapen') db.setMode('Slapen', 'telefoon');
      if (home.action === 'vrij') db.setMode('Vrij', 'telefoon');
      setReply(reply.panel ? `${reply.say} Open het bureau om mee te kijken.` : reply.say);
      window.setTimeout(() => setActivity('idle'), 2400);
    }, 700);
  };
  const stop = () => db.stop('telefoon');
  const resume = () => db.resume('telefoon');
  return <div className="min-h-screen w-full bg-night-950 text-ink md:grid md:place-items-center md:py-16" data-id="element-1965" data-mp="rr7auvjrc84s">
      <div className="hidden md:block" data-id="element-1966" data-mp="wo7fd20yl2ts">
        <MomentControl index={0} count={1} title="Telefoon" onPrev={() => undefined} onNext={() => undefined} data-id="element-1967" data-mp="11nsk5t2t85ef" />
      </div>

      {/* On desktop the phone sits in a device-sized frame; on a phone it is the screen. */}
      <div className="relative mx-auto flex min-h-screen w-full max-w-[430px] flex-col bg-night-950 md:h-[860px] md:min-h-0 md:overflow-hidden md:rounded-[48px] md:border md:border-white/10 md:bg-night-900" data-id="element-1968" data-mp="1ap0drxudsi26">
        <header className="flex items-center justify-between px-6 pt-6" data-id="element-1969" data-mp="3cf255vd3q3tx">
          <time className="font-display text-[20px] font-light tabular-nums text-ink-soft" data-id="element-1970" data-mp="2h5ezwsm6e5yo">{meta.time}</time>
          <div className="flex items-center gap-2" data-id="element-1971" data-mp="2c73q2hvvrvgg">
            <HouseModeChip mode={meta.mode} active data-id="element-1972" data-mp="2c6c8wc31609p" />
            <Link to="/beheer/logboek" aria-label="Beheer" className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1973" data-mp="2r44kit5gi4im">
              <SettingsIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-1974" data-mp="1yd2x64rm12o0" />
            </Link>
          </div>
        </header>

        <main className="no-scrollbar flex-1 overflow-y-auto px-6 pb-40" data-id="element-1975" data-mp="2850g5xov0yxa">
          <section aria-label="Anna" className="flex flex-col items-center pt-4 text-center" data-id="element-1976" data-mp="2873er642w0dm">
            <div className="aspect-square w-[180px]" aria-hidden data-id="element-1977" data-mp="23c79335x5ei1">
              <AnnaAvatar anchor state={avatarState} threads={threads} data-id="element-1978" data-mp="1fno34uacddt4" />
            </div>
            {/* Nothing asked yet: her name, as the boot sequence left it */}
            {!reply && activity === 'idle' && <AnnaWordmark size={40} className="-mt-3" data-id="element-1979" data-mp="3mc6ifxs998s9" />}
            <AnimatePresence mode="wait" data-id="element-1980" data-mp="3r3ergspoas4w">
              <motion.p key={reply ?? 'summary'} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} aria-live="polite" className={`mt-3 max-w-[320px] leading-snug ${reply ? 'font-display text-[22px] font-light text-ink' : 'text-[16px] text-ink-soft'}`} data-id="element-1981" data-mp="3r4ge7ug1x32g">
                {reply ?? (pending.length === 0 ? 'Alles rustig. Niets wacht op je.' : `${pending.length} ${pending.length === 1 ? 'ding wacht' : 'dingen wachten'} op je.`)}
              </motion.p>
            </AnimatePresence>
            {next && !reply && <p className="mt-1 text-[14px] text-ink-muted" data-id="element-1982" data-mp="2opg6u27uacgn">
                Volgende: <span className="tabular-nums text-ink-soft" data-id="element-1983" data-mp="v6h7b89ha9yw">{next.start}</span> {next.title.split(',')[0]}
              </p>}
          </section>

          <section aria-labelledby="ph-mode" className="mt-8" data-id="element-1984" data-mp="q7w7h9y51saz">
            <h2 id="ph-mode" className="text-[13px] font-medium text-ink-muted" data-id="element-1985" data-mp="lfw231zau770">
              Huismodus
            </h2>
            <div className="mt-3" data-id="element-1986" data-mp="ggt6s994ffc8">
              <Segmented label="Huismodus" value={meta.mode} onChange={m => db.setMode(m, 'telefoon')} options={PHONE_MODES.map(m => ({
              value: m,
              label: m
            }))} data-id="element-1987" data-mp="gfqi87z9tozh" />
            </div>
            <p className="mt-2 text-[12px] text-ink-muted" data-id="element-1988" data-mp="2il2thdo3drk">De wand en het bureau volgen meteen.</p>
          </section>

          <section aria-labelledby="ph-waiting" className="mt-8" data-id="element-1989" data-mp="3tpxn459uk7bp">
            <h2 id="ph-waiting" className="text-[13px] font-medium text-ink-muted" data-id="element-1990" data-mp="1wurr9hvknr1t">
              Wacht op jou
            </h2>
            {pending.length > 0 ? <ul className="mt-1 divide-y divide-white/[0.06]" data-id="element-1991" data-mp="20dqjd2au5cwp">
                {pending.map(p => <PhoneProposal key={p.id} proposal={p} onApprove={db.approveProposal} onReject={db.rejectProposal} data-id="element-1992" data-mp="24f3sbl985w7u" />)}
              </ul> : <p className="mt-3 text-[14px] text-ink-soft" data-id="element-1993" data-mp="298561pxp3ppu">Alles is beoordeeld.</p>}
          </section>

          <section aria-labelledby="ph-home" className="mt-8" data-id="element-1994" data-mp="1hew6jyuxmmj6">
            <h2 id="ph-home" className="text-[13px] font-medium text-ink-muted" data-id="element-1995" data-mp="1hgpcfbxq12q4">
              Thuis
            </h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4 text-[14px]" data-id="element-1996" data-mp="1liaxhkkesnbe">
              <div data-id="element-1997" data-mp="1qe9f8pb8f72u">
                <dt className="text-ink-muted" data-id="element-1998" data-mp="1qhxb3v45g5kq">Aanwezig</dt>
                <dd className="mt-0.5 text-ink" data-id="element-1999" data-mp="1vfgm8m5jw28g">{home.length ? home.map(r => r.name).join(', ') : 'Niemand'}</dd>
              </div>
              <div data-id="element-2000" data-mp="31wu4tiba3iyf">
                <dt className="text-ink-muted" data-id="element-2001" data-mp="36tkgltg1vse1">Binnen</dt>
                <dd className="mt-0.5 tabular-nums text-ink" data-id="element-2002" data-mp="36rzla1lww5f4">
                  {(rooms.reduce((s, r) => s + r.temp, 0) / Math.max(1, rooms.length)).toFixed(1).replace('.', ',')} °C
                </dd>
              </div>
              {locks.map(d => <div key={d.id} data-id="element-2003" data-mp="5iton8jxwdlc">
                  <dt className="flex items-center gap-1.5 text-ink-muted" data-id="element-2004" data-mp="5jv2fkscbe6r">
                    <LockIcon className="h-3.5 w-3.5" aria-hidden data-id="element-2005" data-mp="qiruekhx7x0" /> {d.name}
                  </dt>
                  <dd className="mt-0.5 text-ink" data-id="element-2006" data-mp="ffeyq8atgew1">{d.status}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-[12px] text-ink-muted" data-id="element-2007" data-mp="agvau2cnwt11">Sloten en alarm bedien je zelf, in Home Assistant.</p>
          </section>

          <section aria-labelledby="ph-agents" className="mt-8" data-id="element-2008" data-mp="oes870yxx3gr">
            <h2 id="ph-agents" className="text-[13px] font-medium text-ink-muted" data-id="element-2009" data-mp="jil1v72mtpya">
              Aan het werk
            </h2>
            <ul className="mt-3 space-y-3" data-id="element-2010" data-mp="2qacefd2ah5ru">
              {tasks.map(t => <li key={t.id} className="flex items-center gap-3" data-id="element-2011" data-mp="2lb98lqehivit">
                  <AgentMark agentId={t.agent} size="sm" data-id="element-2012" data-mp="10sf5kbjs470g" />
                  <span className="min-w-0 flex-1" data-id="element-2013" data-mp="15qh0btqkhurr">
                    <span className="block truncate text-[14px] text-ink" data-id="element-2014" data-mp="15p5nl4f59coi">{t.title}</span>
                    <span className="block truncate text-[12px] text-ink-muted" data-id="element-2015" data-mp="ry0w4plxjqp9">
                      {stopped ? 'gestopt' : paused.includes(t.agent) ? 'gepauzeerd' : t.stepLabel}
                    </span>
                  </span>
                  <span className="h-1 w-14 shrink-0 overflow-hidden rounded-full bg-white/[0.08]" aria-label={`Stap ${t.step} van ${t.totalSteps}`} data-id="element-2016" data-mp="i2z4nn46mcji">
                    <span className="block h-full rounded-full" style={{
                  width: `${t.step / t.totalSteps * 100}%`,
                  backgroundColor: stopped || paused.includes(t.agent) ? IDLE : getAgent(t.agent).color
                }} data-id="element-2017" data-mp="90zn8tp996g1" />
                  </span>
                </li>)}
            </ul>
          </section>
        </main>

        <div className="absolute inset-x-0 bottom-0 space-y-3 border-t border-white/[0.06] bg-night-950/95 px-4 pb-5 pt-3 backdrop-blur md:bg-night-900/95" data-id="element-2018" data-mp="2cobmd8vf7988">
          <form onSubmit={ask} className="flex items-center gap-2" data-id="element-2019" data-mp="2hg36b0k2mgpe">
            <label htmlFor="ph-ask" className="sr-only" data-id="element-2020" data-mp="22qx6badesmgg">
              Vraag Anna iets
            </label>
            <input id="ph-ask" value={question} onChange={e => setQuestion(e.target.value)} placeholder="Vraag Anna iets" className="h-12 min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.04] px-5 text-[15px] text-ink placeholder:text-ink-muted focus:border-accent/50 focus:outline-none" data-id="element-2021" data-mp="27pqm59g64pjs" />
            <button type="submit" aria-label="Verstuur" disabled={!question.trim()} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-ink text-night-950 transition-opacity duration-150 disabled:opacity-30" data-id="element-2022" data-mp="2qef4bnn9ogrv">
              <SendIcon className="h-5 w-5" data-id="element-2023" data-mp="17fz9ae310ngs" />
            </button>
          </form>
          <div className="flex justify-center" data-id="element-2024" data-mp="12i79kxw53x21">
            <NoodstopButton onTrigger={stop} data-id="element-2025" data-mp="12hfoih065hq0" />
          </div>
        </div>
      </div>

      <NoodstopSheet open={stopped} onResume={resume} stoppedBy={meta.stoppedBy} data-id="element-2026" data-mp="1hdn577cdc10j" />
    </div>;
}